import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
} from '@mui/material';
import { Add as AddIcon, Warning, CheckCircle, Error, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon, Clear as ClearIcon, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { equipmentAPI, suppliersAPI, barriersAPI, devicesAPI } from '../../services/api';
import toast from 'react-hot-toast';

function Equipment() {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const [equipments, setEquipments] = useState([]);
  const [filteredEquipments, setFilteredEquipments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [barriers, setBarriers] = useState([]);
  const [beacons, setBeacons] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    ownership: 'all',
    supplier: 'all',
    status: 'all'
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [formData, setFormData] = useState({
    equipment_name: '',
    equipment_type: '',
    category: 'normal',
    ownership: 'owned',
    supplier_id: '',
    device_id: '',
    validation_date: '',
    expiry_date: '',
    responsible_person: '',
    department: '',
    associated_barriers: [],
    alert_type: 'always',
    notes: ''
  });

  const loadEquipments = useCallback(async () => {
    try {
      const response = await equipmentAPI.getAll();
      console.log('=== EQUIPMENT FRONTEND DEBUG ===');
      console.log('Full API response:', response);
      console.log('Response data:', response?.data);
      console.log('Response data.data:', response?.data?.data);
      
      const equipmentsData = Array.isArray(response?.data?.data) ? response.data.data : 
                            Array.isArray(response?.data) ? response.data : [];
      
      console.log('Processed equipments data:', equipmentsData);
      if (equipmentsData.length > 0) {
        console.log('First equipment sample:', equipmentsData[0]);
        console.log('Supplier name field:', equipmentsData[0].supplier_name);
      }
      
      setEquipments(equipmentsData);
      setFilteredEquipments(equipmentsData);
    } catch (error) {
      const currentTenant = tenantId;
      const mockEquipments = [
        {
          id: '1',
          equipment_name: 'Desfibrilador DEA-001',
          equipment_type: 'medical_device',
          category: 'critical',
          ownership: 'owned',
          supplier_name: 'MedEquip Ltda',
          validation_date: '2024-01-15',
          expiry_date: '2024-12-15',
          status: 'valid',
          responsible_person: 'Dr. Silva',
          department: 'UTI',
          associated_barriers: ['1', '3'],
          alert_type: 'expired_only',
          tenant_id: 'hospital-demo-001'
        },
        {
          id: '2',
          equipment_name: 'Monitor Cardíaco MC-200',
          equipment_type: 'monitoring',
          category: 'critical',
          ownership: 'rented',
          supplier_name: 'CardioTech',
          validation_date: '2024-02-01',
          expiry_date: '2024-08-20',
          status: 'expiring',
          responsible_person: 'Enf. Maria',
          department: 'UTI',
          associated_barriers: ['1'],
          alert_type: 'always',
          tenant_id: 'hospital-demo-001'
        },
        {
          id: '3',
          equipment_name: 'Bomba de Infusão BI-150',
          equipment_type: 'infusion',
          category: 'normal',
          ownership: 'borrowed',
          supplier_name: 'InfusaMed',
          validation_date: '2023-12-01',
          expiry_date: '2024-07-15',
          status: 'expired',
          responsible_person: 'Téc. João',
          department: 'Enfermaria',
          associated_barriers: ['2'],
          alert_type: 'expiring_soon',
          tenant_id: 'hospital-demo-001'
        }
      ].filter(equipment => equipment.tenant_id === currentTenant);
      setEquipments(mockEquipments);
      setFilteredEquipments(mockEquipments);
      console.warn('Equipment API failed, using mock data:', error.message);
    }
  }, [tenantId]);

  useEffect(() => {
    loadEquipments();
    loadSuppliers();
    loadBarriers();
    loadBeacons();
  }, [loadEquipments]);

  const loadSuppliers = async () => {
    try {
      const response = await suppliersAPI.getAll();
      const suppliersData = Array.isArray(response?.data?.data) ? response.data.data : 
                           Array.isArray(response?.data) ? response.data : [];
      setSuppliers(suppliersData);
    } catch (error) {
      const mockSuppliers = [
        { id: '1', name: 'MedEquip Ltda' },
        { id: '2', name: 'CardioTech' },
        { id: '3', name: 'InfusaMed' }
      ];
      setSuppliers(mockSuppliers);
    }
  };

  const loadBarriers = async () => {
    try {
      const response = await barriersAPI.getAll();
      const barriersData = Array.isArray(response?.data?.data) ? response.data.data : 
                          Array.isArray(response?.data) ? response.data : [];
      setBarriers(barriersData);
    } catch (error) {
      const mockBarriers = [
        { id: '1', name: 'Entrada UTI' },
        { id: '2', name: 'Saída Emergência' },
        { id: '3', name: 'Centro Cirúrgico' }
      ];
      setBarriers(mockBarriers);
    }
  };

  const loadBeacons = async () => {
    try {
      const response = await devicesAPI.getAll();
      console.log('=== DEVICES API RESPONSE ===', response);
      const devicesData = Array.isArray(response?.data?.data) ? response.data.data : 
                         Array.isArray(response?.data) ? response.data : [];
      console.log('Total devices:', devicesData.length);
      
      const trackableDevices = devicesData.filter(device => {
        const isTrackableType = ['beacon', 'wristband', 'badge', 'rfid_tag'].includes(device.type);
        const hasIdentifier = device.ble_id || device.epc;
        console.log(`Device ${device.name}: type=${device.type}, trackable=${isTrackableType}, hasId=${hasIdentifier}, ble_id=${device.ble_id}, epc=${device.epc}`);
        return isTrackableType && hasIdentifier;
      });
      
      console.log('Trackable devices count:', trackableDevices.length);
      setBeacons(trackableDevices);
    } catch (error) {
      console.error('Failed to load beacons:', error);
      setBeacons([]);
    }
  };

  const handleEdit = (equipment) => {
    setEditingEquipment(equipment);
    setFormData({
      equipment_name: equipment.equipment_name,
      equipment_type: equipment.equipment_type,
      category: equipment.category,
      ownership: equipment.ownership,
      supplier_id: suppliers.find(s => s.name === equipment.supplier_name)?.id || '',
      device_id: equipment.device_id || '',
      validation_date: equipment.validation_date,
      expiry_date: equipment.expiry_date,
      responsible_person: equipment.responsible_person,
      department: equipment.department,
      associated_barriers: Array.isArray(equipment.associated_barriers) ? equipment.associated_barriers : [],
      alert_type: equipment.alert_type || 'always',
      notes: equipment.notes || ''
    });
    setOpen(true);
  };

  const handleDelete = async (equipmentId) => {
    if (window.confirm('Tem certeza que deseja excluir este equipamento?')) {
      try {
        await equipmentAPI.delete(equipmentId);
        toast.success('Equipamento excluído com sucesso!');
        await loadEquipments();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Erro ao excluir equipamento');
      }
    }
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingEquipment(null);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const equipmentData = {
        ...formData,
        supplier_name: suppliers.find(s => s.id === formData.supplier_id)?.name || ''
      };
      
      if (editingEquipment) {
        await equipmentAPI.update(editingEquipment.id, equipmentData);
        toast.success('Equipamento atualizado com sucesso!');
      } else {
        await equipmentAPI.create(equipmentData);
        toast.success('Equipamento criado com sucesso!');
      }
      handleCloseDialog();
      await loadEquipments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar equipamento');
    }
  };

  const resetForm = () => {
    setFormData({
      equipment_name: '',
      equipment_type: '',
      category: 'normal',
      ownership: 'owned',
      supplier_id: '',
      device_id: '',
      validation_date: '',
      expiry_date: '',
      responsible_person: '',
      department: '',
      associated_barriers: [],
      alert_type: 'always',
      notes: ''
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'associated_barriers' ? (Array.isArray(value) ? value : []) : value,
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid': return <CheckCircle color="success" />;
      case 'expiring': return <Warning color="warning" />;
      case 'expired': return <Error color="error" />;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'valid': return 'success';
      case 'expiring': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'critical': return 'error';
      case 'normal': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getOwnershipColor = (ownership) => {
    switch (ownership) {
      case 'owned': return 'success';
      case 'rented': return 'warning';
      case 'borrowed': return 'info';
      case 'consignment': return 'secondary';
      default: return 'default';
    }
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };

  const applyFilters = (currentFilters) => {
    let filtered = equipments;

    if (currentFilters.name) {
      filtered = filtered.filter(equipment => 
        equipment.equipment_name.toLowerCase().includes(currentFilters.name.toLowerCase())
      );
    }

    if (currentFilters.ownership !== 'all') {
      filtered = filtered.filter(equipment => equipment.ownership === currentFilters.ownership);
    }

    if (currentFilters.supplier !== 'all') {
      filtered = filtered.filter(equipment => equipment.supplier_name === currentFilters.supplier);
    }

    if (currentFilters.status !== 'all') {
      filtered = filtered.filter(equipment => equipment.status === currentFilters.status);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      if (sortBy === 'name') {
        aValue = a.equipment_name.toLowerCase();
        bValue = b.equipment_name.toLowerCase();
      } else if (sortBy === 'expiry_date') {
        aValue = new Date(a.expiry_date || '9999-12-31');
        bValue = new Date(b.expiry_date || '9999-12-31');
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredEquipments(filtered);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const clearFilters = () => {
    setFilters({ name: '', ownership: 'all', supplier: 'all', status: 'all' });
    setFilteredEquipments(equipments);
  };

  useEffect(() => {
    applyFilters(filters);
  }, [equipments, sortBy, sortOrder]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {t('equipment.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          {t('equipment.newEquipment')}
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              size="small"
              label="Buscar por nome"
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Propriedade</InputLabel>
              <Select
                value={filters.ownership}
                label="Propriedade"
                onChange={(e) => handleFilterChange('ownership', e.target.value)}
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="owned">Próprio</MenuItem>
                <MenuItem value="rented">Alugado</MenuItem>
                <MenuItem value="borrowed">Emprestado</MenuItem>
                <MenuItem value="consignment">Consignado</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Fornecedor</InputLabel>
              <Select
                value={filters.supplier}
                label="Fornecedor"
                onChange={(e) => handleFilterChange('supplier', e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                {Array.from(new Set(equipments.map(e => e.supplier_name))).filter(Boolean).map((supplierName) => (
                  <MenuItem key={supplierName} value={supplierName}>
                    {supplierName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="valid">Válido</MenuItem>
                <MenuItem value="expiring">Vencendo</MenuItem>
                <MenuItem value="expired">Vencido</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              size="small"
            >
              Limpar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Estatísticas */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Equipamentos
              </Typography>
              <Typography variant="h4">
                {filteredEquipments.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Válidos
              </Typography>
              <Typography variant="h4" color="success.main">
                {filteredEquipments.filter(e => {
                  if (!e.expiry_date) return false;
                  const today = new Date();
                  const expiryDate = new Date(e.expiry_date);
                  const diffDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
                  return diffDays > 30;
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Vencendo
              </Typography>
              <Typography variant="h4" color="warning.main">
                {filteredEquipments.filter(e => {
                  if (!e.expiry_date) return false;
                  const today = new Date();
                  const expiryDate = new Date(e.expiry_date);
                  const diffDays = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
                  return diffDays >= 0 && diffDays <= 30;
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Vencidos
              </Typography>
              <Typography variant="h4" color="error.main">
                {filteredEquipments.filter(e => {
                  if (!e.expiry_date) return false;
                  const today = new Date();
                  const expiryDate = new Date(e.expiry_date);
                  return expiryDate < today;
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                  Equipamento
                  {sortBy === 'name' && (sortOrder === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                </Box>
              </TableCell>
              <TableCell>Categoria</TableCell>
              <TableCell>Propriedade</TableCell>
              <TableCell>Fornecedor</TableCell>
              <TableCell>
                <Box display="flex" alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => handleSort('expiry_date')}>
                  Vencimento
                  {sortBy === 'expiry_date' && (sortOrder === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />)}
                </Box>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Responsável</TableCell>
              <TableCell>Departamento</TableCell>
              <TableCell>Barreiras</TableCell>
              <TableCell>Tipo Alerta</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(filteredEquipments) && filteredEquipments.length > 0 ? filteredEquipments.map((equipment) => (
              <TableRow key={equipment.id}>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">{equipment.equipment_name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {equipment.equipment_type}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`equipment.${equipment.category}`)}
                    color={getCategoryColor(equipment.category)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`equipment.${equipment.ownership}`)}
                    color={getOwnershipColor(equipment.ownership)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>{equipment.supplier_name}</TableCell>
                <TableCell>
                  {equipment.expiry_date ? new Date(equipment.expiry_date).toLocaleDateString('pt-BR') : '-'}
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    {getStatusIcon(equipment.status)}
                    <Chip
                      label={t(`common.${equipment.status}`)}
                      color={getStatusColor(equipment.status)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </TableCell>
                <TableCell>{equipment.responsible_person}</TableCell>
                <TableCell>{equipment.department}</TableCell>
                <TableCell>
                  {equipment.associated_barriers?.length > 0 ? (
                    equipment.associated_barriers.map(barrierId => {
                      const barrier = barriers.find(b => b.id === barrierId);
                      return barrier ? (
                        <Chip
                          key={barrierId}
                          label={barrier.name}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                          color="primary"
                          variant="outlined"
                        />
                      ) : null;
                    })
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      {t('common.none')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      equipment.alert_type === 'always' ? t('equipment.always') :
                      equipment.alert_type === 'expired_only' ? t('equipment.expiredOnly') :
                      equipment.alert_type === 'expiring_soon' ? t('equipment.expiringSoon') : t('equipment.never')
                    }
                    size="small"
                    color={
                      equipment.alert_type === 'always' ? 'primary' :
                      equipment.alert_type === 'expired_only' ? 'error' :
                      equipment.alert_type === 'expiring_soon' ? 'warning' : 'default'
                    }
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEdit(equipment)}
                    sx={{ mr: 1 }}
                  >
                    Editar
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(equipment.id)}
                  >
                    Excluir
                  </Button>
                </TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    {equipments.length === 0 ? 'Nenhum equipamento cadastrado' : 'Nenhum equipamento encontrado com os filtros aplicados'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingEquipment ? 'Editar Equipamento' : 'Novo Equipamento'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="equipment_name"
                  label="Nome do Equipamento"
                  fullWidth
                  required
                  value={formData.equipment_name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="equipment_type"
                  label="Tipo"
                  fullWidth
                  value={formData.equipment_type}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Categoria</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                  >
                    <MenuItem value="critical">Crítico</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="low">Baixo</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Propriedade</InputLabel>
                  <Select
                    name="ownership"
                    value={formData.ownership}
                    onChange={handleChange}
                  >
                    <MenuItem value="owned">Próprio</MenuItem>
                    <MenuItem value="rented">Alugado</MenuItem>
                    <MenuItem value="borrowed">Emprestado</MenuItem>
                    <MenuItem value="consignment">Consignado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Fornecedor</InputLabel>
                  <Select
                    name="supplier_id"
                    value={formData.supplier_id}
                    onChange={handleChange}
                  >
                    {Array.isArray(suppliers) && suppliers.map((supplier) => (
                      <MenuItem key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="validation_date"
                  label="Data de Validação"
                  type="date"
                  fullWidth
                  required
                  value={formData.validation_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="expiry_date"
                  label="Data de Vencimento"
                  type="date"
                  fullWidth
                  required
                  value={formData.expiry_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="responsible_person"
                  label="Responsável"
                  fullWidth
                  value={formData.responsible_person}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="department"
                  label="Departamento"
                  fullWidth
                  value={formData.department}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Dispositivo Rastreável Associado</InputLabel>
                  <Select
                    name="device_id"
                    value={formData.device_id}
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>Nenhum dispositivo</em>
                    </MenuItem>
                    {Array.isArray(beacons) && beacons.map((beacon) => (
                      <MenuItem key={beacon.id} value={beacon.id}>
                        [{beacon.type.toUpperCase()}] {beacon.ble_id || beacon.epc} - {beacon.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Barreiras Associadas</InputLabel>
                  <Select
                    name="associated_barriers"
                    multiple
                    value={formData.associated_barriers}
                    onChange={handleChange}
                  >
                    {Array.isArray(barriers) && barriers.map((barrier) => (
                      <MenuItem key={barrier.id} value={barrier.id}>
                        {barrier.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Alerta</InputLabel>
                  <Select
                    name="alert_type"
                    value={formData.alert_type}
                    onChange={handleChange}
                  >
                    <MenuItem value="always">Sempre ao passar</MenuItem>
                    <MenuItem value="expired_only">Apenas quando vencido</MenuItem>
                    <MenuItem value="expiring_soon">Vencendo em 30 dias</MenuItem>
                    <MenuItem value="never">Nunca alertar</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editingEquipment ? 'Atualizar' : 'Salvar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default Equipment;